import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  LayoutPanelLeft, Plus, Loader2, Trash2, Edit2, ExternalLink, Copy, Eye, MousePointerClick, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  id: null, title: '', slug: '', headline: '', subtext: '', image_url: '',
  button_text: 'Get instant access', brand_color: '#db2777', collect_first_name: false,
  email_list_id: '', lead_tag: 'funnel', success_mode: 'message',
  success_message: "You're in! Check your inbox shortly.", redirect_url: '', status: 'published',
};

export const FunnelBuilderPage = () => {
  const { api } = useAuth();
  const [funnels, setFunnels] = useState([]);
  const [emailLists, setEmailLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [f, l] = await Promise.all([
        api.get('/funnels'),
        api.get('/email/lists').catch(() => ({ data: { lists: [] } })),
      ]);
      setFunnels(f.data?.funnels || []);
      setEmailLists(l.data?.lists || (Array.isArray(l.data) ? l.data : []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (f) => { setForm({ ...f }); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Give the page a name');
    if (!form.headline.trim()) return toast.error('Add a headline');
    if (form.success_mode === 'redirect' && !form.redirect_url.trim()) return toast.error('Add a redirect URL or switch to a thank-you message');
    setSaving(true);
    try {
      if (form.id) { await api.put(`/funnels/${form.id}`, form); toast.success('Page updated'); }
      else { await api.post('/funnels', form); toast.success('Page created'); }
      setDialogOpen(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not save page'));
    } finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete page "${f.title}"?`)) return;
    try { await api.delete(`/funnels/${f.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      // Fallback for restricted clipboard contexts (insecure origin / denied permission)
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Link copied');
      } catch {
        toast.error('Could not copy — copy it manually');
      }
    }
  };

  const brand = form.brand_color || '#db2777';

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="funnel-builder-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <LayoutPanelLeft className="w-7 h-7 text-pink-600" /> Funnel Builder
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Hosted lead-capture pages. Point your Instagram DM link here to turn comments into email subscribers.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-funnel-btn" className="bg-pink-600 hover:bg-pink-700">
          <Plus className="w-4 h-4 mr-2" /> New page
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-pink-600" /></div>
      ) : funnels.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center text-zinc-500">
            <LayoutPanelLeft className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium text-zinc-700">No landing pages yet</p>
            <p className="text-sm mt-1">Create a page, then use its link as the DM URL in your comment automation rules.</p>
            <Button onClick={openNew} className="mt-4 bg-pink-600 hover:bg-pink-700"><Plus className="w-4 h-4 mr-2" />New page</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="funnels-list">
          {funnels.map((f) => (
            <Card key={f.id} data-testid={`funnel-card-${f.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{f.title}</span>
                      <Badge variant="outline" className={`text-xs ${f.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-100 text-zinc-500'}`}>{f.status}</Badge>
                    </div>
                    <a href={f.public_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1 break-all" data-testid={`funnel-url-${f.id}`}>
                      /p/{f.slug} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => copyUrl(f.public_url)} title="Copy link" data-testid={`copy-${f.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`edit-${f.id}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(f)} className="text-red-500" data-testid={`delete-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-zinc-50 p-2">
                    <div className="text-xs text-zinc-400 flex items-center justify-center gap-1"><Eye className="w-3 h-3" />views</div>
                    <div className="font-semibold text-zinc-800" data-testid={`views-${f.id}`}>{f.views}</div>
                  </div>
                  <div className="rounded-lg bg-zinc-50 p-2">
                    <div className="text-xs text-zinc-400 flex items-center justify-center gap-1"><MousePointerClick className="w-3 h-3" />signups</div>
                    <div className="font-semibold text-zinc-800" data-testid={`submits-${f.id}`}>{f.submits}</div>
                  </div>
                  <div className="rounded-lg bg-zinc-50 p-2">
                    <div className="text-xs text-zinc-400 flex items-center justify-center gap-1"><TrendingUp className="w-3 h-3" />conv.</div>
                    <div className="font-semibold text-zinc-800">{f.conversion_rate}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="funnel-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit landing page' : 'New landing page'}</DialogTitle>
            <DialogDescription>Capture emails from your IG DM link. Edits update the live page instantly.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Form */}
            <div className="space-y-4">
              <div>
                <Label>Page name (internal)</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Free Guide lead magnet" data-testid="funnel-title-input" />
              </div>
              <div>
                <Label>Custom link (slug)</Label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-zinc-400">/p/</span>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="free-guide" data-testid="funnel-slug-input" />
                </div>
                <p className="text-xs text-zinc-400 mt-1">Leave blank to auto-generate from the name.</p>
              </div>
              <div>
                <Label>Headline</Label>
                <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Get the free GUIDE" data-testid="funnel-headline-input" />
              </div>
              <div>
                <Label>Sub-text</Label>
                <Textarea value={form.subtext} onChange={(e) => setForm({ ...form, subtext: e.target.value })} rows={2} placeholder="Drop your email and I'll send it over." data-testid="funnel-subtext-input" />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…/banner.jpg" data-testid="funnel-image-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Button text</Label>
                  <Input value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} data-testid="funnel-button-input" />
                </div>
                <div>
                  <Label>Brand color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-9 w-12 rounded border border-zinc-200 cursor-pointer" data-testid="funnel-color-input" />
                    <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="flex-1" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label>Also collect first name</Label>
                <Switch checked={form.collect_first_name} onCheckedChange={(v) => setForm({ ...form, collect_first_name: v })} data-testid="funnel-firstname-toggle" />
              </div>
              <div>
                <Label>Add subscribers to list</Label>
                <Select value={form.email_list_id || 'none'} onValueChange={(v) => setForm({ ...form, email_list_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="funnel-list-select"><SelectValue placeholder="No list" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No list (just collect)</SelectItem>
                    {emailLists.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>After signup</Label>
                <Select value={form.success_mode} onValueChange={(v) => setForm({ ...form, success_mode: v })}>
                  <SelectTrigger data-testid="funnel-success-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="message">Show a thank-you message</SelectItem>
                    <SelectItem value="redirect">Redirect to a URL</SelectItem>
                  </SelectContent>
                </Select>
                {form.success_mode === 'message' ? (
                  <Input className="mt-2" value={form.success_message} onChange={(e) => setForm({ ...form, success_message: e.target.value })} placeholder="You're in! Check your inbox." data-testid="funnel-message-input" />
                ) : (
                  <Input className="mt-2" value={form.redirect_url} onChange={(e) => setForm({ ...form, redirect_url: e.target.value })} placeholder="https://yoursite.com/download" data-testid="funnel-redirect-input" />
                )}
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label>Published (live)</Label>
                <Switch checked={form.status === 'published'} onCheckedChange={(v) => setForm({ ...form, status: v ? 'published' : 'draft' })} data-testid="funnel-status-toggle" />
              </div>
            </div>

            {/* Live preview */}
            <div>
              <Label className="text-xs text-zinc-400 uppercase tracking-wide">Live preview</Label>
              <div className="mt-2 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 sticky top-0" data-testid="funnel-preview">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {form.image_url ? (
                    <img src={form.image_url} alt="" className="w-full h-28 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="h-1.5" style={{ backgroundColor: brand }} />
                  )}
                  <div className="p-5">
                    <h3 className="font-bold text-zinc-900 leading-tight">{form.headline || 'Your headline here'}</h3>
                    {form.subtext && <p className="text-sm text-zinc-500 mt-2 whitespace-pre-line">{form.subtext}</p>}
                    <div className="mt-4 space-y-2">
                      {form.collect_first_name && <div className="h-9 rounded-lg border border-zinc-200 px-3 flex items-center text-sm text-zinc-300">First name</div>}
                      <div className="h-9 rounded-lg border border-zinc-200 px-3 flex items-center text-sm text-zinc-300">you@email.com</div>
                      <div className="h-9 rounded-lg text-white text-sm font-semibold flex items-center justify-center" style={{ backgroundColor: brand }}>{form.button_text || 'Subscribe'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="save-funnel-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (form.id ? 'Save changes' : 'Create page')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FunnelBuilderPage;
